import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IgnoreJobRequest,
  MarkProvisionedRequest,
  ResolveManualRequest,
} from '@control-center/shared';

import type { ReactivateAccessRequest, SuspendAccessRequest } from '@control-center/shared';

import {
  fetchFailedJobs,
  fetchProvisioningJobs,
  ignoreJob,
  markProvisioned,
  reactivateAccess,
  reprocessJob,
  resolveJobManual,
  suspendAccess,
  type FailedJobsFilters,
} from '../lib/provisioning-api';

export const provisioningKeys = {
  all: ['provisioning'] as const,
  list: (status: string) => [...provisioningKeys.all, 'list', status] as const,
  failed: (filters: FailedJobsFilters) => [...provisioningKeys.all, 'failed', filters] as const,
};

export function useProvisioningJobs(
  status: 'pending' | 'in_progress' | 'provisioned' | 'failed' = 'pending',
) {
  return useQuery({
    queryKey: provisioningKeys.list(status),
    queryFn: () => fetchProvisioningJobs(status),
    staleTime: 30_000,
  });
}

export function useMarkProvisioned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, body }: { jobId: string; body: MarkProvisionedRequest }) =>
      markProvisioned(jobId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: provisioningKeys.all }),
  });
}

// Story 12.7 — DLQ de provisioning.

export function useFailedJobs(filters: Omit<FailedJobsFilters, 'cursor'> = {}) {
  return useInfiniteQuery({
    queryKey: provisioningKeys.failed(filters),
    queryFn: ({ pageParam }) => fetchFailedJobs({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

/**
 * Contagem total de falhas de provisioning pro badge do sidebar (AC7).
 * Gated por role (só admin/financeiro acessam o endpoint da DLQ). Revalida a cada 60s.
 */
export function useFailedProvisioningCount(enabled = true) {
  return useQuery<number, Error>({
    queryKey: [...provisioningKeys.all, 'failed-count'],
    queryFn: async () => {
      const res = await fetchFailedJobs({ limit: 1 });
      return res.data.total;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useReprocessJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => reprocessJob(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: provisioningKeys.all }),
  });
}

export function useResolveJobManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, body }: { jobId: string; body: ResolveManualRequest }) =>
      resolveJobManual(jobId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: provisioningKeys.all }),
  });
}

export function useIgnoreJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, body }: { jobId: string; body: IgnoreJobRequest }) =>
      ignoreJob(jobId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: provisioningKeys.all }),
  });
}

// Story 12.10 — override manual de acesso. Invalida ['clients'] (status atualiza sem reload).

export function useSuspendAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: string; body: SuspendAccessRequest }) =>
      suspendAccess(clientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: provisioningKeys.all });
    },
  });
}

export function useReactivateAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: string; body: ReactivateAccessRequest }) =>
      reactivateAccess(clientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: provisioningKeys.all });
    },
  });
}
